import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFormData } from "../../contexts/FormDataContext";
import { usePositions } from "../../contexts/PositionsContext";
import { useLanguage } from "../../contexts";
import TooltipGray from "../TooltipGray";
import { WeeklyScheduleTable, COURSE_SCHEDULE_WEEK_FIELDS } from "../WeeklyScheduleTable";

function AutoGrowTextarea({ value, onChange, placeholder, id, rows = 12 }) {
  const textareaRef = useRef(null);
  const [minHeight, setMinHeight] = useState(0);

  useEffect(() => {
    if (!textareaRef.current) return;
    if (!minHeight) {
      setMinHeight(textareaRef.current.scrollHeight);
    }
  }, [minHeight]);

  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = "auto";
    const nextHeight = Math.max(
      element.scrollHeight,
      minHeight || element.scrollHeight
    );
    element.style.height = `${nextHeight}px`;
  }, [value, minHeight]);

  return (
    <textarea
      id={id}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      ref={textareaRef}
      rows={rows}
      placeholder={placeholder}
      className="block w-full rounded-md px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-patras-buccaneer/70 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-patras-buccaneer focus:ring-offset-0 focus:ring-patras-buccaneer dark:bg-[var(--color-bg-surface)] dark:text-[var(--color-text-primary)] dark:outline-[var(--color-border)] dark:placeholder:text-[var(--color-text-muted)] dark:focus:outline-[var(--color-primary)] dark:focus:ring-[var(--color-primary)]"
    />
  );
}

const COURSE_PLAN_FIELDS = [
  {
    key: "generalDescription",
    labelKey: "courses.planGeneralDescription",
    placeholderKey: "courses.phGeneralDescription",
  },
  {
    key: "learningObjectives",
    labelKey: "courses.planLearningObjectives",
    placeholderKey: "courses.phLearningObjectives",
  },
  {
    key: "deliveryMethods",
    labelKey: "courses.planDeliveryMethods",
    placeholderKey: "courses.phDeliveryMethods",
  },
  {
    key: "bibliographyMaterial",
    labelKey: "courses.planBibliography",
    placeholderKey: "courses.phBibliography",
  },
  {
    key: "learningOutcomes",
    labelKey: "courses.planLearningOutcomes",
    placeholderKey: "courses.phLearningOutcomes",
  },
  {
    key: "assessmentMethodsCriteria",
    labelKey: "courses.planAssessment",
    placeholderKey: "courses.phAssessment",
  },
];

const ALL_REQUIRED_FIELDS = [...COURSE_PLAN_FIELDS, ...COURSE_SCHEDULE_WEEK_FIELDS];

export default function CoursePlanSection() {
  const { formData, handleCoursePlanFieldChange } = useFormData();
  const { positions = [] } = usePositions();
  const { t } = useLanguage();
  const tabsViewportRef = useRef(null);
  const [activeCourseId, setActiveCourseId] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const selectedPosition = useMemo(
    () => positions.find((position) => String(position.id) === String(formData.positionId)) || null,
    [positions, formData.positionId]
  );

  const courses = Array.isArray(selectedPosition?.courses) ? selectedPosition.courses : [];

  const updateScrollControls = () => {
    const el = tabsViewportRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }
    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 1);
  };

  useEffect(() => {
    updateScrollControls();
    const el = tabsViewportRef.current;
    if (!el) return;

    const onScroll = () => updateScrollControls();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateScrollControls);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateScrollControls);
    };
  }, [courses.length]);

  useEffect(() => {
    if (!courses.length) {
      setActiveCourseId(null);
      return;
    }

    const exists = courses.some((course) => String(course.id) === String(activeCourseId));
    if (!activeCourseId || !exists) {
      setActiveCourseId(courses[0].id);
    }
  }, [courses, activeCourseId]);

  const activeCourse = useMemo(
    () => courses.find((course) => String(course.id) === String(activeCourseId)) || courses[0] || null,
    [courses, activeCourseId]
  );

  const scrollTabsBy = (offset) => {
    const el = tabsViewportRef.current;
    if (!el) return;
    el.scrollBy({ left: offset, behavior: "smooth" });
  };

  const isCourseComplete = (courseId) => {
    const coursePlan = (formData.coursePlans || {})[String(courseId)] || {};
    return ALL_REQUIRED_FIELDS.every((field) => {
      const value = coursePlan[field.key];
      return typeof value === "string" && value.trim().length > 0;
    });
  };

  if (!selectedPosition) {
    return (
      <section className="space-y-3">
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-[var(--color-warning)] dark:bg-[var(--color-warning)] dark:text-[var(--color-warning)]">
          {t("courses.selectFieldFirst")}
        </p>
      </section>
    );
  }

  if (!courses.length) {
    return (
      <section className="space-y-3">
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-[var(--color-warning)] dark:bg-[var(--color-warning)] dark:text-[var(--color-warning)]">
          {t("courses.noCoursesForPosition")}
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-5">
      <div className="space-y-2">
        <div>
          <label className="block text-sm/6 font-medium text-gray-900 dark:text-[var(--color-text-primary)]">
            {t("courses.fieldCourses")}{" "}
            <span className="font-semibold underline text-patras-buccaneer dark:text-[var(--color-primary)]">
              {selectedPosition?.scientificField || ""}
            </span>{" "}
            {t("courses.totalCount", { count: courses.length })}
            <TooltipGray content={t("courses.planTooltip")}>
              <span className="ml-2 inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full border border-gray-300 text-xs text-gray-600 dark:border-[var(--color-border)] dark:text-[var(--color-text-muted)]">
                i
              </span>
            </TooltipGray>
          </label>
        </div>

        <div className="rounded-lg border border-patras-buccaneer bg-white p-2 shadow-sm dark:bg-[var(--color-bg-card)] dark:border-[var(--color-border)] dark:shadow-[0_12px_30px_var(--color-shadow-soft)]">
          <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => scrollTabsBy(-220)}
            disabled={!canScrollLeft}
            className="h-8 w-8 shrink-0 rounded-full border border-gray-300 text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-[var(--color-border)] dark:text-[var(--color-text-secondary)]"
            aria-label={t("courses.prevTabs")}
          >
            {"<"}
          </button>

          <div ref={tabsViewportRef} className="flex-1 overflow-x-auto scroll-smooth" style={{ scrollbarWidth: "none" }}>
            <div className="flex min-w-max items-center gap-2 pr-1">
              {courses.map((course, index) => {
                const isActive = String(course.id) === String(activeCourse?.id);
                const isComplete = isCourseComplete(course.id);
                return (
                  <button
                    key={course.id}
                    type="button"
                    onClick={() => setActiveCourseId(course.id)}
                    className={`inline-flex w-max shrink-0 whitespace-nowrap rounded-md border px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "border-patras-buccaneer bg-patras-buccaneer text-white dark:border-[var(--color-primary)] dark:bg-[var(--color-primary)] dark:text-[var(--color-text-inverse)]"
                        : isComplete
                            ? "border-green-300 text-green-800 hover:border-green-500"
                          : "border-gray-300 bg-white text-gray-700 hover:border-patras-buccaneer dark:border-[var(--color-border)] dark:bg-[var(--color-bg-card)] dark:text-[var(--color-text-secondary)] dark:hover:border-[var(--color-primary)]"
                    }`}
                    title={course.name || t("courses.courseFallback", { n: index + 1 })}
                  >
                    <span className="inline-flex items-center gap-1 whitespace-nowrap">
                      {course.name || t("courses.courseFallback", { n: index + 1 })}
                      {isComplete ? <span aria-hidden="true">✓</span> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            onClick={() => scrollTabsBy(220)}
            disabled={!canScrollRight}
            className="h-8 w-8 shrink-0 rounded-full border border-gray-300 text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-[var(--color-border)] dark:text-[var(--color-text-secondary)]"
            aria-label={t("courses.nextTabs")}
          >
            {">"}
          </button>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <div>
          <label className="block text-sm/6 font-medium text-gray-900 dark:text-[var(--color-text-primary)]">
            {t("courses.scheduleFor")}{" "}
            <span className="font-semibold underline text-patras-buccaneer dark:text-[var(--color-primary)]">
              {activeCourse?.name || t("courses.untitled")}
            </span>{" "}
            {t("courses.courseCounter", { current: courses.findIndex((course) => String(course.id) === String(activeCourse?.id)) + 1, total: courses.length })}
          </label>
        </div>

        {activeCourse ? (
          <article key={activeCourse.id}>
            <div className="rounded-md border border-patras-buccaneer p-4 md:p-5 dark:border-[var(--color-border)] dark:bg-[var(--color-bg-card)]">
              <div className="grid grid-cols-1 gap-4">
                {COURSE_PLAN_FIELDS.map((field) => {
                  const coursePlan = (formData.coursePlans || {})[String(activeCourse.id)] || {};
                  return (
                    <div key={`${activeCourse.id}-${field.key}`} className="space-y-3 pb-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-[var(--color-text-secondary)]">
                        {t(field.labelKey)} <span className="text-red-500">*</span>
                      </label>
                      <AutoGrowTextarea
                        id={`course-plan-${activeCourse.id}-${field.key}`}
                        value={coursePlan[field.key] || ""}
                        onChange={(nextValue) =>
                          handleCoursePlanFieldChange(activeCourse.id, field.key, nextValue)
                        }
                        placeholder={t(field.placeholderKey)}
                        rows={2}
                      />
                    </div>
                  );
                })}

                <WeeklyScheduleTable
                  courseId={activeCourse.id}
                  coursePlans={formData.coursePlans || {}}
                  onFieldChange={handleCoursePlanFieldChange}
                />
              </div>
            </div>
          </article>
        ) : null}
      </div>
    </section>
  );
}